import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, SlidersHorizontal } from 'lucide-react'
import { listAuditLogs } from '../../api/audit'
import Spinner from '../../components/Spinner'
import { SettingsHeader, SettingsTabs } from '../settings/settingsShared'

// Entries per page; the log loads up to `limit` at once and pages through them here.
const PAGE_SIZE = 25

export default function AuditTrail() {
  const [searchParams] = useSearchParams()
  const [rows, setRows] = useState([])
  const [actor, setActor] = useState('')
  const [action, setAction] = useState('')
  const [objectType, setObjectType] = useState('')
  const [objectId, setObjectId] = useState('')
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [q, setQ] = useState('')
  const [limit, setLimit] = useState('200')
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [more, setMore] = useState(false)

  const run = async (params) => {
    setLoading(true)
    try {
      const data = await listAuditLogs(params)
      setRows(data || [])
      setPage(1)
    } catch { setRows([]) }
    setLoading(false)
  }
  const load = async () => {
    await run({ actor, action, object_type: objectType, object_id: objectId, start, end, q, limit: parseInt(limit || '200', 10) })
  }
  useEffect(() => {
    const next = {
      actor: searchParams.get('actor') || '',
      action: searchParams.get('action') || '',
      object_type: searchParams.get('object_type') || '',
      object_id: searchParams.get('object_id') || '',
      start: searchParams.get('start') || '',
      end: searchParams.get('end') || '',
      q: searchParams.get('q') || '',
      limit: searchParams.get('limit') || '200',
    }
    setActor(next.actor)
    setAction(next.action)
    setObjectType(next.object_type)
    setObjectId(next.object_id)
    setStart(next.start)
    setEnd(next.end)
    setQ(next.q)
    setLimit(next.limit)
    if (next.action || next.object_type || next.object_id) setMore(true)
    run({ ...next, limit: parseInt(next.limit || '200', 10) })
  }, [searchParams])

  const pages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE))
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)
  const capped = rows.length >= (parseInt(limit || '200', 10) || 200)

  return (
    <div className="ci-page">
      <SettingsTabs />
      <SettingsHeader title="Audit log" meta="Who changed what, and when." />

      <form className="ci-toolbar set-audit-filters" onSubmit={(e) => { e.preventDefault(); load() }}>
        <label className="ci-search" aria-label="Search the audit log">
          <Search size={14} />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search the summaries…" />
        </label>
        <input className="ci-select" value={actor} onChange={(e) => setActor(e.target.value)} placeholder="Person" aria-label="Person" />
        <span className="list-dates">
          <input type="date" className="ci-select" value={start} onChange={(e) => setStart(e.target.value)} aria-label="From" />
          <span>to</span>
          <input type="date" className="ci-select" value={end} onChange={(e) => setEnd(e.target.value)} aria-label="To" />
        </span>
        <button type="button" className="ci-btn ci-btn-ghost" aria-expanded={more} onClick={() => setMore((m) => !m)}>
          <SlidersHorizontal size={14} />{more ? 'Fewer filters' : 'More filters'}
        </button>
        <div className="ci-toolbar-right">
          <button type="submit" className="ci-btn ci-btn-primary" disabled={loading}>{loading ? 'Searching…' : 'Search'}</button>
        </div>
        {more && (
          <div className="set-more-filters">
            <input className="ci-select" value={action} onChange={(e) => setAction(e.target.value)} placeholder="Action, e.g. sales.*" aria-label="Action" />
            <input className="ci-select" value={objectType} onChange={(e) => setObjectType(e.target.value)} placeholder="Record type" aria-label="Record type" />
            <input className="ci-select set-narrow" value={objectId} onChange={(e) => setObjectId(e.target.value)} placeholder="Record #" aria-label="Record number" />
            <label className="set-inline-label">Show up to
              <input type="number" min="1" className="ci-select set-narrow" value={limit} onChange={(e) => setLimit(e.target.value)} />
            </label>
          </div>
        )}
      </form>

      <div className="ci-table-wrap">
        <div className="ci-table-scroll">
          <table className="ci-table">
            <thead>
              <tr><th>When</th><th>Who</th><th>Action</th><th>Record</th><th>Summary</th><th className="hide-sm">Route</th><th className="hide-sm">IP</th></tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7"><Spinner label="Loading the audit log…" /></td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="7"><div className="ci-empty">Nothing found for these filters.</div></td></tr>
              ) : pageRows.map((r, i) => (
                <tr key={r.id ?? i}>
                  <td className="set-nowrap">{r.occurred_at}</td>
                  <td>{r.actor_id ? <Link className="mf-row-link" to={`/master/users/${r.actor_id}`}>{r.actor_name || r.actor_id}</Link> : (r.actor_name || '–')}</td>
                  <td>{r.action}</td>
                  <td className="set-nowrap">{[r.object_type, r.object_id].filter(Boolean).join(' #') || '–'}</td>
                  <td><span className="set-clamp" title={r.summary}>{r.summary}</span></td>
                  <td className="hide-sm ci-muted-cell"><span className="set-clamp" title={r.route}>{r.route}</span></td>
                  <td className="hide-sm ci-muted-cell">{r.ip}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && rows.length > 0 && (
          <div className="ci-pagination">
            <span className="ci-page-info">
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}{capped ? ' (the latest; narrow the filters to see older entries)' : ''}
            </span>
            {pages > 1 && (
              <div className="ci-page-btns">
                <button type="button" className="ci-page-btn" disabled={page === 1} onClick={() => setPage((n) => n - 1)}>Previous</button>
                <button type="button" className="ci-page-btn" disabled={page === pages} onClick={() => setPage((n) => n + 1)}>Next</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
